'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/react/shallow';
import { toasterApi } from '@terra/ui-design-system';

import { CopilotSessionToast } from '../../components/chat/CopilotSessionToast';
import { switchToChatTab } from '../../components/chat/actions/chatActions';
import { subscribeToSessionCompletion } from '../../store/chatInstancesStore';
import { useChatTabsStore } from '../../store/chatTabsStore';
import { useLayoutStore } from '../../store/layoutStore';
import { useUnreadTabsStore } from '../../store/unreadTabsStore';

const isUserViewingCopilotSession = ({
  tabId,
  activeChatTabId,
  isChatCollapsed,
  rightPanelView,
  showSidePanels,
}: {
  tabId: string;
  activeChatTabId: string | null;
  isChatCollapsed: boolean;
  rightPanelView: 'collaboration' | 'copilot';
  showSidePanels: boolean;
}) =>
  tabId === activeChatTabId &&
  showSidePanels &&
  !isChatCollapsed &&
  rightPanelView === 'copilot';

const expandCopilotPanel = () => {
  const layoutStore = useLayoutStore.getState();
  layoutStore.setSidePanelsVisible(true);
  layoutStore.setChatCollapsed(false);
  layoutStore.setRightPanelView('copilot');
};

const handleSessionCompleted = ({
  chatId,
  sessionCompletedLabels,
}: {
  chatId: string;
  sessionCompletedLabels: {
    brandLabel: string;
    statusLabel: string;
    closeLabel: string;
  };
}) => {
  const { chatTabs, activeChatTabId } = useChatTabsStore.getState();
  const tab = chatTabs.find((chatTab) => chatTab.chatId === chatId);
  if (!tab) {
    return;
  }

  const { isChatCollapsed, rightPanelView, showSidePanels } =
    useLayoutStore.getState();

  if (
    isUserViewingCopilotSession({
      tabId: tab.tabId,
      activeChatTabId,
      isChatCollapsed,
      rightPanelView,
      showSidePanels,
    })
  ) {
    return;
  }

  useUnreadTabsStore.getState().markTabUnread(tab.tabId);

  const toastId = `copilot-session-${tab.tabId}`;
  toasterApi.create({
    id: toastId,
    duration: 5000,
    meta: {
      render: (
        <CopilotSessionToast
          toastId={toastId}
          title={tab.title}
          brandLabel={sessionCompletedLabels.brandLabel}
          statusLabel={sessionCompletedLabels.statusLabel}
          closeLabel={sessionCompletedLabels.closeLabel}
          onOpen={() => {
            switchToChatTab({ tabId: tab.tabId });
            expandCopilotPanel();
          }}
        />
      ),
    },
  });
};

export function useSessionCompletionNotifier() {
  const t = useTranslations('chat.copilot');
  const tCommon = useTranslations('designSystem.common');
  const sessionCompletedLabels = {
    brandLabel: t('sessionCompletedBrand'),
    statusLabel: t('sessionCompletedStatus'),
    closeLabel: tCommon('close'),
  };
  const sessionCompletedLabelsRef = useRef(sessionCompletedLabels);
  sessionCompletedLabelsRef.current = sessionCompletedLabels;

  const activeChatTabId = useChatTabsStore((state) => state.activeChatTabId);
  const { isChatCollapsed, rightPanelView, showSidePanels } = useLayoutStore(
    useShallow((state) => ({
      isChatCollapsed: state.isChatCollapsed,
      rightPanelView: state.rightPanelView,
      showSidePanels: state.showSidePanels,
    })),
  );

  useEffect(() => {
    if (!activeChatTabId) {
      return;
    }
    if (
      isUserViewingCopilotSession({
        tabId: activeChatTabId,
        activeChatTabId,
        isChatCollapsed,
        rightPanelView,
        showSidePanels,
      })
    ) {
      useUnreadTabsStore.getState().markTabRead(activeChatTabId);
    }
  }, [activeChatTabId, isChatCollapsed, rightPanelView, showSidePanels]);

  useEffect(() => {
    return subscribeToSessionCompletion((chatId) => {
      handleSessionCompleted({
        chatId,
        sessionCompletedLabels: sessionCompletedLabelsRef.current,
      });
    });
  }, []);
}
